// Thin wrappers for serverless admin endpoints.
// All service-key operations happen server-side — no admin client in the browser.
import type { FeatureOverrides } from "../context/SubscriptionContext";
import { supabase } from "./supabase";

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();

  // If token is missing or expires within the next 60 seconds, force a refresh
  const expiresAt: number = (session as any)?.expires_at ?? 0;
  if (!session || expiresAt * 1000 < Date.now() + 60_000) {
    const { data: { session: fresh }, error } = await supabase.auth.refreshSession();
    if (error || !fresh) throw new Error("Not authenticated");
    return fresh.access_token;
  }

  return session.access_token;
}

async function post(url: string, body: Record<string, unknown>) {
  const token = await getToken();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
  return json;
}

// ── Supervisor: agent management ───────────────────────────────────────────
export const agentApi = {
  create: (params: {
    email: string; password: string; name: string;
    zone?: string; phone?: string; target?: number; avatar?: string;
  }) => post("/api/agent-manage", { action: "create", ...params }),

  delete: (agentId: string) =>
    post("/api/agent-manage", { action: "delete", agentId }),

  setActive: (agentId: string, active: boolean) =>
    post("/api/agent-manage", { action: "set_active", agentId, active }),

  resetPassword: (agentId: string, password: string) =>
    post("/api/agent-manage", { action: "reset_password", agentId, password }),
};

// ── Platform admin: management operations ─────────────────────────────────
export const adminApi = {
  listUsers: () =>
    post("/api/admin-manage", { action: "list_users" }),

  resetPassword: (userId: string, password: string) =>
    post("/api/admin-manage", { action: "reset_password", userId, password }),

  deleteOwner: (ownerId: string) =>
    post("/api/admin-manage", { action: "delete_owner", ownerId }),

  saveAccess: (ownerId: string, overrides: FeatureOverrides, toUnlock: string[], toLock: string[]) =>
    post("/api/admin-manage", { action: "save_access", ownerId, overrides, toUnlock, toLock }),

  createOwner: (params: { name: string; business_name?: string; email: string; phone?: string }) =>
    post("/api/admin-manage", { action: "create_owner", ...params }),

  listCustomPlans: () =>
    post("/api/admin-manage", { action: "list_custom_plans" }),

  createCustomPlan: (params: { owner_id: string; name: string; price: number; max_agents?: number | null; max_shops?: number | null; features?: Record<string, boolean> }) =>
    post("/api/admin-manage", { action: "create_custom_plan", ...params }),

  updateCustomPlan: (params: { plan_id: string; name: string; price: number; max_agents?: number | null; max_shops?: number | null; features?: Record<string, boolean> }) =>
    post("/api/admin-manage", { action: "update_custom_plan", ...params }),

  deleteCustomPlan: (plan_id: string) =>
    post("/api/admin-manage", { action: "delete_custom_plan", plan_id }),

  activateCustomPlan: (plan_id: string, owner_id: string) =>
    post("/api/admin-manage", { action: "activate_custom_plan", plan_id, owner_id }),

  changePlan: (owner_id: string, plan_id: string) =>
    post("/api/admin-manage", { action: "change_plan", owner_id, plan_id }),

  listPayments: () =>
    post("/api/admin-manage", { action: "list_payments" }),

  forceActivatePayment: (paymentId: string) =>
    post("/api/admin-manage", { action: "force_activate_payment", paymentId }),

  rejectPayment: (paymentId: string) =>
    post("/api/admin-manage", { action: "reject_payment", paymentId }),

  lockOwner: (ownerId: string) =>
    post("/api/admin-manage", { action: "lock_owner", ownerId }),

  unlockOwner: (ownerId: string) =>
    post("/api/admin-manage", { action: "unlock_owner", ownerId }),

  getSettings: () =>
    post("/api/admin-manage", { action: "get_settings" }),

  updateSetting: (key: string, value: unknown) =>
    post("/api/admin-manage", { action: "update_setting", key, value }),

  getMonitorSnapshot: () =>
    post("/api/admin-manage", { action: "get_monitor_snapshot" }),

  listLiveSellers: () =>
    post("/api/admin-manage", { action: "list_live_sellers" }),

  listSystemErrors: () =>
    post("/api/admin-manage", { action: "list_system_errors" }),

  listActivityEvents: (params: { from?: number; to?: number; severity?: string; category?: string; unreadOnly?: boolean }) =>
    post("/api/admin-manage", { action: "list_activity_events", ...params }),

    getDayTotals: (dateKey: string) =>
    post("/api/admin-manage", { action: "get_day_totals", dateKey }),

  getPresence: (dateKey: string) =>
    post("/api/admin-manage", { action: "get_presence", dateKey }),

  listSystemEvents: (params: {
    from: number;
    to: number;
    category?: string;
    severity?: string;
    actorId?: string;
    shopId?: string;
    search?: string;
    since?: string;
    until?: string;
  }) =>
    post("/api/admin-manage", { action: "list_system_events", ...params }),

  markEventsRead: (eventId?: string) =>
    post("/api/admin-manage", { action: "mark_events_read", ...(eventId ? { eventId } : {}) }),

  listLiveTransactions: () =>
    post("/api/admin-manage", { action: "list_live_transactions" }),

  getPricingConfig: () =>
    post("/api/admin-manage", { action: "get_pricing_config" }),


  updatePricingConfig: (params: {
    id: string;
    base_fee: number; agent_rate: number; shop_rate: number;
    agent_tier2_min: number; agent_tier2_pct: number;
    agent_tier3_min: number; agent_tier3_pct: number;
    shop_tier2_min: number;  shop_tier2_pct: number;
    shop_tier3_min: number;  shop_tier3_pct: number;
  }) => post("/api/admin-manage", { action: "update_pricing_config", ...params }),

  getTrialDays: () =>
    post("/api/admin-manage", { action: "get_trial_days" }),
  
  updateTrialDays: (days: number) =>
    post("/api/admin-manage", { action: "update_trial_days", days }),

  getPlatformCounts: () =>
    post("/api/admin-manage", { action: "get_platform_counts" }),

  logAdminAccess: (path: string, admin_name?: string) =>
    post("/api/admin-manage", { action: "log_admin_access", path, admin_name }),

  listAdminAccesses: () =>
    post("/api/admin-manage", { action: "list_admin_accesses" }),
};
